import { alpha, Theme } from "@mui/material/styles";
import type { CSSObject } from "@mui/material/styles";

/**
 * Scrollbar design system.
 *
 * Three categories, so scroll regions across the app read as one system instead
 * of each screen inventing its own `::-webkit-scrollbar` block:
 *
 * | Category                        | Use                                        |
 * |---------------------------------|--------------------------------------------|
 * | `appScrollbar(theme)`           | page / large scroll regions — 8px          |
 * | `compactScrollbar(theme)`       | cards, legends, popovers, dialogs — 6px    |
 * | `hiddenScrollbar()`             | sidebar — scrolls, shows nothing           |
 *
 * Colours are derived from the active palette (`text.secondary`), so they follow
 * light/dark mode and any configured GT Brokers theme colour. Nothing here is a
 * hardcoded hex.
 */

/** Thumb tint. Dark mode needs a little more presence against a dark surface. */
const thumbColor = (theme: Theme, weight: number) =>
  alpha(theme.palette.text.secondary, theme.palette.mode === "dark" ? weight + 0.08 : weight);

/**
 * Page-level scrollbar: slim (8px), rounded, muted, transparent track,
 * darker on hover. Deliberately visible — the page must still look scrollable.
 */
export const appScrollbar = (theme: Theme): CSSObject => ({
  scrollbarWidth: "thin",
  scrollbarColor: `${thumbColor(theme, 0.28)} transparent`,
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: thumbColor(theme, 0.28),
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: thumbColor(theme, 0.45),
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },
});

/**
 * Card/compact scrollbar: 6px, lower contrast than the page bar so an inner list
 * never competes with the page. Still visible at rest — a legend that scrolls
 * with no indication that it scrolls is worse than a thin bar.
 */
export const compactScrollbar = (theme: Theme): CSSObject => ({
  scrollbarWidth: "thin",
  scrollbarColor: `${thumbColor(theme, 0.2)} transparent`,
  "&::-webkit-scrollbar": {
    width: 6,
    height: 6,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: thumbColor(theme, 0.2),
    borderRadius: 6,
    transition: "background-color 160ms ease",
  },
  "&:hover::-webkit-scrollbar-thumb": {
    backgroundColor: thumbColor(theme, 0.34),
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: thumbColor(theme, 0.45),
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "transparent",
  },
});

/**
 * Sidebar: no visible track or thumb, full scroll behaviour retained. Wheel,
 * touch, keyboard and programmatic scrolling all still work — only the painted
 * scrollbar is suppressed. Apply to the single scroll container, never globally.
 */
export const hiddenScrollbar = (): CSSObject => ({
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    width: 0,
    height: 0,
    display: "none",
  },
});

/**
 * `MuiCssBaseline` overrides: the page scrollbar plus a compact default for any
 * inner scroll region that has not opted into a category. Component-level `sx`
 * rules generate a class and therefore still win over these.
 */
export const scrollbarBaseline = (theme: Theme): CSSObject => ({
  html: appScrollbar(theme),
  body: appScrollbar(theme),
  "*": compactScrollbar(theme),
});
